/**
 * Lay out a printed dive slate: up to six QR codes on one page, each with the
 * diver's own title and the conditions it was built for.
 *
 * This is a pure function. Given the same slots it produces the same points, on
 * any machine, with no PDF library loaded. `pdf.ts` turns the result into a
 * file; a preview canvas could consume the identical ops.
 *
 * The sizes are driven by one hard constraint: a HERO reads a code reliably
 * from about 40 mm across. Everything else — how much of a title fits, whether
 * the command prints at all — gives way to keeping the code above that.
 */

import { encode } from "../qr";
import type { CompiledSlot } from "../shelf";
import { truncate, wrapMono } from "./metrics";
import { MM, PAGE_SIZES, type DrawOp, type Page, type PageSizeKey } from "./ops";

/**
 * What shape of page a slate type needs. Threaded through rather than
 * hard-coded so a future module (DESIGN.md §6, "DiveSlate as a shell, GoPro
 * Labs as a module") is not stuck with a grid and a legibility floor that are
 * specific to reading a QR code off a wet page — a checklist module, for
 * instance, has neither constraint. Every call below defaults to
 * `GOPRO_GRID`, so nothing about today's app changes; this only stops the
 * next module from having to touch `layout.ts` to get its own numbers.
 */
export interface GridSpec {
  cols: number;
  rows: number;
  /** Below this the camera starts to struggle, especially on wet paper. */
  minQrMm: number;
}

/** Falls out of one number: a HERO reads a QR code reliably from about
 *  40 mm across, and 40 mm × six is what fits on A4. See DESIGN.md §5. */
export const GOPRO_GRID: GridSpec = { cols: 2, rows: 3, minQrMm: 40 };

// Kept as the constants existing callers (SlateDialog.tsx, slate.test.ts)
// already import. Derived from GOPRO_GRID rather than restated, so the two
// can never drift apart while there is only one grid in the app.
export const SLOTS_PER_PAGE = GOPRO_GRID.cols * GOPRO_GRID.rows;
export const MIN_QR_MM = GOPRO_GRID.minQrMm;

const INK = {
  graphite: "#21272B",
  graphite2: "#5A646A",
  line: "#A9B1A8",
  deep: "#0E4A6E",
  paper: "#FFFFFF",
} as const;

export interface SlateOptions {
  pageSize: PageSizeKey;
  /** Printed above the grid. A dive site, a trip, a boat. */
  heading: string;
  subheading: string;
  /** The raw Labs command under each code. Useful for typing one in by hand or
   *  checking what a code actually does; drop it for the largest possible QR. */
  showCommand: boolean;
  /** Dashed cut guides, for turning the page into six laminated tags. */
  showCutLines: boolean;
}

export const DEFAULT_SLATE_OPTIONS: SlateOptions = {
  pageSize: "a4",
  heading: "Dive slate",
  subheading: "",
  showCommand: true,
  showCutLines: true,
};

export interface SlateLayout {
  pages: Page[];
  /** Side of the printed code, in millimetres. Surfaced in the UI so the diver
   *  knows before printing whether it will scan. */
  qrMm: number;
  /** Slots left out because they did not compile. */
  skipped: CompiledSlot[];
}


// --- geometry -------------------------------------------------------------
//
// One source of truth for the page grid. `measureQrMm` and `layoutSlate` both
// read it, so the number the dialog promises and the number the PDF prints
// cannot drift apart.

const PAD = 9;
const TITLE_SIZE = 14;
const SUMMARY_GAP = 11;
const QR_TOP_GAP = 6;
const COMMAND_SIZE = 5.4;
const COMMAND_LEADING = COMMAND_SIZE + 1.4;
const MAX_COMMAND_LINES = 3;

interface Geometry {
  pageW: number;
  pageH: number;
  margin: number;
  headerH: number;
  footerH: number;
  gutter: number;
  gridX: number;
  gridY: number;
  cellW: number;
  cellH: number;
}

function geometry(pageSize: PageSizeKey, grid: GridSpec): Geometry {
  const size = PAGE_SIZES[pageSize];
  const margin = 10 * MM;
  const headerH = 52;
  const footerH = 22;
  const gutter = 10;
  const gridW = size.widthPt - margin * 2;
  const gridH = size.heightPt - margin * 2 - headerH - footerH;

  return {
    pageW: size.widthPt,
    pageH: size.heightPt,
    margin,
    headerH,
    footerH,
    gutter,
    gridX: margin,
    gridY: margin + headerH,
    cellW: (gridW - gutter * (grid.cols - 1)) / grid.cols,
    cellH: (gridH - gutter * (grid.rows - 1)) / grid.rows,
  };
}

/** Height reserved under the code for the printed command. */
function commandBlockHeight(lines: number): number {
  return lines ? lines * COMMAND_LEADING + 4 : 0;
}

/** How many lines the printed command needs, capped. */
function commandLinesFor(payload: string, cellW: number): number {
  return Math.min(wrapMono(payload, cellW - PAD * 2, COMMAND_SIZE).length, MAX_COMMAND_LINES);
}

/**
 * Lines to reserve under every code on a page. One figure for the whole page,
 * taken from its longest command: a grid of six identical squares is legible,
 * a grid of six nearly-identical squares looks like a mistake.
 */
function reservedLinesFor(
  slots: readonly CompiledSlot[],
  cellW: number,
  showCommand: boolean,
): number {
  if (!showCommand) return 0;
  return slots.reduce((most, s) => Math.max(most, commandLinesFor(s.payload, cellW)), 1);
}

/** Side of the code inside a cell, in points. */
function qrSideIn(g: Geometry, commandLines: number): number {
  const textBlock = PAD + TITLE_SIZE * 0.78 + SUMMARY_GAP + QR_TOP_GAP;
  const availH = g.cellH - textBlock - PAD - commandBlockHeight(commandLines);
  return Math.max(0, Math.min(g.cellW - PAD * 2, availH));
}

export function layoutSlate(
  slots: readonly CompiledSlot[],
  options: SlateOptions = DEFAULT_SLATE_OPTIONS,
  grid: GridSpec = GOPRO_GRID,
): SlateLayout {
  const printable = slots.filter((s) => s.ok && s.payload.length > 0);
  const skipped = slots.filter((s) => !s.ok || s.payload.length === 0);

  const g = geometry(options.pageSize, grid);
  const slotsPerPage = grid.cols * grid.rows;

  const pages: Page[] = [];
  let qrSide = 0;

  for (let p = 0; p * slotsPerPage < Math.max(printable.length, 1); p++) {
    const pageSlots = printable.slice(p * slotsPerPage, (p + 1) * slotsPerPage);
    const reserved = reservedLinesFor(pageSlots, g.cellW, options.showCommand);
    const ops: DrawOp[] = [];

    ops.push({ op: "rect", x: 0, y: 0, w: g.pageW, h: g.pageH, fill: INK.paper });
    ops.push(...header(options, g, pageSlots.length));

    pageSlots.forEach((slot, i) => {
      const col = i % grid.cols;
      const row = Math.floor(i / grid.cols);
      const x = g.gridX + col * (g.cellW + g.gutter);
      const y = g.gridY + row * (g.cellH + g.gutter);
      const cell = drawCell(slot, x, y, g, options, reserved);
      qrSide = cell.qrSide;
      ops.push(...cell.ops);
    });

    ops.push(...footer(g, options, p, printable.length));

    pages.push({ widthPt: g.pageW, heightPt: g.pageH, ops });
  }

  return { pages, qrMm: round1(qrSide / MM), skipped };
}

// --- pieces ---------------------------------------------------------------

function header(options: SlateOptions, g: Geometry, count: number): DrawOp[] {
  const ops: DrawOp[] = [];
  const baseline = g.margin + 20;

  ops.push({
    op: "text",
    x: g.margin,
    y: baseline,
    text: truncate(options.heading.toUpperCase(), g.pageW * 0.55, "display", 22, "bold", 0.4),
    size: 22,
    family: "display",
    weight: "bold",
    color: INK.graphite,
    align: "left",
    tracking: 0.4,
  });

  if (options.subheading.trim()) {
    ops.push({
      op: "text",
      x: g.margin,
      y: baseline + 12,
      text: truncate(options.subheading, g.pageW * 0.6, "body", 9),
      size: 9,
      family: "body",
      weight: "normal",
      color: INK.graphite2,
      align: "left",
    });
  }

  ops.push({
    op: "text",
    x: g.pageW - g.margin,
    y: baseline,
    text: `${count} preset${count === 1 ? "" : "s"} \u00b7 GoPro Labs`,
    size: 9,
    family: "body",
    weight: "normal",
    color: INK.graphite2,
    align: "right",
  });

  ops.push({
    op: "line",
    x1: g.margin,
    y1: g.gridY - 14,
    x2: g.pageW - g.margin,
    y2: g.gridY - 14,
    color: INK.graphite,
    lineWidth: 1.2,
  });

  return ops;
}

function drawCell(
  slot: CompiledSlot,
  x: number,
  y: number,
  g: Geometry,
  options: SlateOptions,
  reservedLines: number,
): { ops: DrawOp[]; qrSide: number } {
  const ops: DrawOp[] = [];
  const w = g.cellW;
  const h = g.cellH;

  if (options.showCutLines) {
    ops.push({ op: "rect", x, y, w, h, stroke: INK.line, lineWidth: 0.5, dash: [3, 3] });
  }

  // --- title block, top of the cell ---------------------------------------
  const titleBaseline = y + PAD + TITLE_SIZE * 0.78;
  ops.push({
    op: "text",
    x: x + PAD,
    y: titleBaseline,
    // Room reserved on the right for the camera badge.
    text: truncate(slot.title, w - PAD * 2 - 38, "display", TITLE_SIZE, "bold", 0.3),
    size: TITLE_SIZE,
    family: "display",
    weight: "bold",
    color: INK.graphite,
    align: "left",
    tracking: 0.3,
  });

  // Which body a code is for is the first thing you need and the easiest thing
  // to forget once six of them are laminated together.
  ops.push({
    op: "text",
    x: x + w - PAD,
    y: titleBaseline,
    text: slot.conditions.camera,
    size: 7.5,
    family: "mono",
    weight: "bold",
    color: INK.deep,
    align: "right",
  });

  const summaryBaseline = titleBaseline + SUMMARY_GAP;
  ops.push({
    op: "text",
    x: x + PAD,
    y: summaryBaseline,
    text: truncate(slot.summary, w - PAD * 2, "body", 7.5),
    size: 7.5,
    family: "body",
    weight: "normal",
    color: INK.graphite2,
    align: "left",
  });

  // --- command, bottom of the cell ----------------------------------------
  let commandLines: string[] = [];
  if (options.showCommand) {
    commandLines = wrapMono(slot.payload, w - PAD * 2, COMMAND_SIZE);
    // Past three lines the command stops being a reference and starts eating
    // the code. The QR is the artefact; the text is the note in the margin.
    if (commandLines.length > MAX_COMMAND_LINES) {
      commandLines = commandLines.slice(0, MAX_COMMAND_LINES);
      const last = commandLines[MAX_COMMAND_LINES - 1]!;
      commandLines[MAX_COMMAND_LINES - 1] = last.slice(0, Math.max(0, last.length - 1)) + "\u2026";
    }
  }

  // --- QR, filling whatever is left ---------------------------------------
  const qrSide = qrSideIn(g, reservedLines);
  const availTop = summaryBaseline + QR_TOP_GAP;
  const availH = h - (availTop - y) - PAD - commandBlockHeight(reservedLines);
  const qrX = x + (w - qrSide) / 2;
  const qrY = availTop + (availH - qrSide) / 2;

  ops.push({ op: "rect", x: qrX, y: qrY, w: qrSide, h: qrSide, fill: INK.paper });
  ops.push({
    op: "qr",
    x: qrX,
    y: qrY,
    size: qrSide,
    // Two modules rather than the specified four: the cell already has white
    // space and a dashed cut line around it, which the scanner reads as quiet
    // zone anyway. The modules bought back go into a bigger code.
    quietModules: 2,
    matrix: encode(slot.payload, "M"),
  });

  commandLines.forEach((line, i) => {
    ops.push({
      op: "text",
      x: x + PAD,
      y: y + h - PAD - (commandLines.length - 1 - i) * COMMAND_LEADING,
      text: line,
      size: COMMAND_SIZE,
      family: "mono",
      weight: "normal",
      color: INK.graphite2,
      align: "left",
    });
  });

  return { ops, qrSide };
}

function footer(
  g: Geometry,
  options: SlateOptions,
  pageIndex: number,
  total: number,
): DrawOp[] {
  const baseline = g.pageH - g.margin - g.footerH + 14;
  const note = options.showCutLines
    ? "Cut on the dashed lines and laminate. Hold the camera about 150 mm away until the LED blinks twice."
    : "Hold the camera about 150 mm away until the LED blinks twice.";

  const ops: DrawOp[] = [
    {
      op: "text",
      x: g.margin,
      y: baseline,
      text: truncate(note, g.pageW - g.margin * 2 - 90, "body", 7),
      size: 7,
      family: "body",
      weight: "normal",
      color: INK.graphite2,
      align: "left",
    },
  ];

  if (total > SLOTS_PER_PAGE) {
    ops.push({
      op: "text",
      x: g.pageW - g.margin,
      y: baseline,
      text: `Page ${pageIndex + 1} of ${Math.ceil(total / SLOTS_PER_PAGE)}`,
      size: 7,
      family: "body",
      weight: "normal",
      color: INK.graphite2,
      align: "right",
    });
  }

  return ops;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Width of the printed code, in millimetres, before anything is drawn. Lets the
 * export dialog warn about a code too small to scan while the diver is still
 * choosing options rather than after they have used a sheet of paper.
 */
export function measureQrMm(
  pageSize: PageSizeKey,
  showCommand: boolean,
  slots: readonly CompiledSlot[] = [],
  grid: GridSpec = GOPRO_GRID,
): number {
  const g = geometry(pageSize, grid);
  const reserved = slots.length
    ? reservedLinesFor(slots.slice(0, grid.cols * grid.rows), g.cellW, showCommand)
    : showCommand
      ? MAX_COMMAND_LINES
      : 0;
  return round1(qrSideIn(g, reserved) / MM);
}
